import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { reportStatus } from '@helpers';

import {
  createReportFolder,
  deleteReportFolders,
  deleteReports,
  readReportDetails,
  readReportFolders,
  readReportItems,
  readReportPrompts,
  shareReportFolderWithClientGroup,
  shareReportFolderWithUsers,
  shareReportWithClientGroup,
  shareReportWithUsers,
  updateReportFavorite,
  updateReportFolder,
  updateReportFolderName,
  updateReportName,
} from '@api/reports';

import ItemAnswers from '../../components/ItemAnswers';
import ListPage from '../../components/ListPage';

import AddToFolder from '../../components/ListPage/components/ActionsToolbar/components/AddToFolder';
import Automate from '../../components/ListPage/components/ActionsToolbar/components/Automate';
import BulkDownload from '../../components/ListPage/components/ActionsToolbar/components/BulkDownload';
import Delete from '../../components/ListPage/components/ActionsToolbar/components/Delete';
import Details from '../../components/ListPage/components/ActionsToolbar/components/Details';
import ReportDownload from '../../components/ListPage/components/ActionsToolbar/components/ReportDownload';
import Favorite from '../../components/ListPage/components/ActionsToolbar/components/Favorite';
import Rename from '../../components/ListPage/components/ActionsToolbar/components/Rename';
import Reprompt from '../../components/ListPage/components/ActionsToolbar/components/Reprompt';
import Share from '../../components/ListPage/components/ActionsToolbar/components/Share';

import { FOLDER_COLUMNS, getFilterOptions, ITEM_COLUMNS } from './utilities/helpers';

const Reports = () => {
  const canAccessAskWhy = useSelector((state) => state?.user?.data?.permissions?.canAccessAskWhy);
  const canShareReportsPublicly = useSelector(
    (state) => state?.user?.data?.permissions?.canShareReportsPublicly
  );
  const isWebsocketConnected = useSelector((state) => state?.websocket?.isConnected);
  const filterOptions = getFilterOptions({ canAccessAskWhy });
  const navigate = useNavigate();

  return (
    <ListPage
      createFolder={createReportFolder}
      filterOptions={filterOptions}
      folderActions={[
        <Rename
          key="rename-folder"
          type="Report"
          updateName={({ name, row }) => updateReportFolderName({ id: row.id, name })}
        />,
        <Share
          key="share-folder"
          shareWithClient={shareReportFolderWithClientGroup}
          shareWithUsers={shareReportFolderWithUsers}
        />,
        <BulkDownload key="bulk-download-folder" />,
        <Delete
          deleteItems={(selectedRows) =>
            deleteReportFolders({ ids: selectedRows.map(({ id }) => id) })
          }
          key="delete-folder"
        />,
      ]}
      folderColumns={FOLDER_COLUMNS}
      isItemsPollingEnabled={!isWebsocketConnected}
      itemActions={[
        <Details key="details-item" />,
        <Rename
          key="rename-item"
          type="Report"
          updateName={({ name, row }) => updateReportName({ id: row.id, name })}
        />,
        <AddToFolder
          createFolder={createReportFolder}
          key="add-item-to-folder"
          readFolders={readReportFolders}
          updateFolder={updateReportFolder}
        />,
        <ReportDownload key="report-download-item" />,
        <Favorite key="favorite-item" updateFavorite={updateReportFavorite} />,
        <Share
          hasSharePublicly={canShareReportsPublicly}
          key="share-item"
          shareWithClient={shareReportWithClientGroup}
          shareWithUsers={shareReportWithUsers}
        />,
        <Automate key="automate-item" />,
        <Reprompt key="reprompt-item" />,
        <BulkDownload key="bulk-download-item" />,
        <Delete
          deleteItems={(selectedRows) => deleteReports({ ids: selectedRows.map(({ id }) => id) })}
          key="delete-item"
        />,
      ]}
      itemColumns={ITEM_COLUMNS}
      onCreate={() => navigate('/dashboard/reports/')}
      onExpandItemRow={() => {}}
      readFolders={readReportFolders}
      readItems={readReportItems}
      renderRowExpanded={({ row }) => (
        <ItemAnswers
          item={row?.original}
          readItemAnswers={readReportDetails}
          readPrompts={readReportPrompts}
          type="Report"
        />
      )}
      shouldPollItem={(item) => item?.status < reportStatus.COMPLETED}
      type="Report"
      workspace="Reports"
    />
  );
};

export default Reports;
